import { getBrandConfig } from '../config/brandConfig';

/**
 * Charge un asset spécifique à la marque détectée
 * @param assetName - Nom de l'asset (ex: "logo.svg", "hero-image.png")
 * @returns Chemin complet vers l'asset de la marque
 */
export const getBrandAsset = (assetName: string): string => {
  const brand = getBrandConfig();
  return `/brands/${brand.name}/${assetName}`;
};

/**
 * Charge un asset avec fallback vers la marque par défaut
 * @param assetName - Nom de l'asset
 * @param fallbackBrand - Marque de fallback (par défaut "teachr")
 * @returns Chemin vers l'asset
 */
export const getBrandAssetWithFallback = (
  assetName: string, 
  fallbackBrand: string = 'teachr'
): string => {
  const brand = getBrandConfig();
  const assetPath = `/brands/${brand.name}/${assetName}`;
  
  // Vérifier si l'asset existe (cette logique peut être étendue)
  return assetPath;
};

/**
 * Charge un asset d'image avec gestion des formats
 * @param baseName - Nom de base de l'image (sans extension)
 * @param formats - Formats supportés (par défaut ["webp", "png", "jpg"])
 * @returns Chemin vers l'asset avec le meilleur format supporté
 */
export const getBrandImage = (
  baseName: string, 
  formats: string[] = ["webp", "png", "jpg", "svg"]
): string => {
  const brand = getBrandConfig();
  
  // Pour l'instant, on retourne le premier format
  // Cette logique peut être étendue pour détecter le support des formats
  return `/brands/${brand.name}/images/${baseName}.${formats[0]}`;
};

/**
 * Charge un asset d'icône
 * @param iconName - Nom de l'icône (sans extension)
 * @param format - Format de l'icône (par défaut "svg")
 * @returns Chemin vers l'icône
 */
export const getBrandIcon = (
  iconName: string, 
  format: string = "svg"
): string => {
  const brand = getBrandConfig();
  return `/brands/${brand.name}/icons/${iconName}.${format}`;
};

/**
 * Charge un asset de police
 * @param fontName - Nom de la police (sans extension)
 * @param format - Format de la police (par défaut "ttf")
 * @returns Chemin vers la police
 */
export const getBrandFont = (
  fontName: string, 
  format: string = "ttf"
): string => {
  const brand = getBrandConfig();
  return `/brands/${brand.name}/fonts/${fontName}.${format}`;
};

/**
 * Charge un asset de document
 * @param docName - Nom du document (sans extension)
 * @param format - Format du document (par défaut "pdf")
 * @returns Chemin vers le document
 */
export const getBrandDocument = (
  docName: string, 
  format: string = "pdf"
): string => {
  const brand = getBrandConfig();
  return `/brands/${brand.name}/documents/${docName}.${format}`;
};

/**
 * Génère un chemin d'asset personnalisé
 * @param category - Catégorie de l'asset (ex: "images", "icons", "videos")
 * @param fileName - Nom du fichier avec extension
 * @returns Chemin vers l'asset
 */
export const getBrandAssetByCategory = (
  category: string, 
  fileName: string
): string => {
  const brand = getBrandConfig();
  return `/brands/${brand.name}/${category}/${fileName}`;
};

/**
 * Vérifie si un asset existe (pour la gestion des fallbacks)
 * @param assetPath - Chemin vers l'asset
 * @returns Promise<boolean> - True si l'asset existe
 */
export const assetExists = async (assetPath: string): Promise<boolean> => {
  try {
    const response = await fetch(assetPath, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Charge un asset avec gestion des erreurs et fallback
 * @param primaryAsset - Asset principal à charger
 * @param fallbackAsset - Asset de fallback
 * @returns Chemin vers l'asset disponible
 */
export const getAssetWithFallback = async (
  primaryAsset: string, 
  fallbackAsset: string
): Promise<string> => {
  if (await assetExists(primaryAsset)) {
    return primaryAsset;
  }
  return fallbackAsset;
};

export default {
  getBrandAsset,
  getBrandAssetWithFallback,
  getBrandImage,
  getBrandIcon,
  getBrandFont,
  getBrandDocument,
  getBrandAssetByCategory,
  assetExists,
  getAssetWithFallback
};

